import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { devService } from '../../../services/dev.service';
import Loader from '../../../components/common/Loader/Loader';
import { ChevronDown, ChevronRight, Copy, Check, Globe, AlertCircle, Trash2, Loader2, ExternalLink } from 'lucide-react';
import './DevTool.css';

const DevTool = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const [activeTab, setActiveTab] = useState('domains'); // Default: domains tab
  const [logs, setLogs] = useState([]);
  const [domains, setDomains] = useState([]);
  // Everything that hit trouble fetching its article - the URLs that never
  // became news, and the news the user's browser had to fetch because the
  // server was refused. One list: the error is what they have in common.
  const [erroredNews, setErroredNews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedLogId, setExpandedLogId] = useState(null);
  const [expandedDomainId, setExpandedDomainId] = useState(null);
  const [copiedLogId, setCopiedLogId] = useState(null);
  const [copiedLinkId, setCopiedLinkId] = useState(null);
  const [deletingLinkId, setDeletingLinkId] = useState(null);
  const [deletingDomainId, setDeletingDomainId] = useState(null);
  const [confirmDeleteDomainId, setConfirmDeleteDomainId] = useState(null);
  const [expandedErroredId, setExpandedErroredId] = useState(null);
  const [deletingErroredId, setDeletingErroredId] = useState(null);
  const [clearingErrored, setClearingErrored] = useState(false);
  // Armed-but-not-yet-confirmed deletes, shown inline like the deck cards
  const [confirmingErroredId, setConfirmingErroredId] = useState(null);
  const [confirmingClearAll, setConfirmingClearAll] = useState(false);

  // Refs for details panels
  const logsDetailsRef = useRef(null);
  const domainsDetailsRef = useRef(null);
  const erroredDetailsRef = useRef(null);

  // Fetch data based on active tab
  useEffect(() => {
    if (!isAdmin) return;

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        if (activeTab === 'errored') {
          const response = await devService.getErroredNews();
          const fetched = response.erroredNews || [];
          setErroredNews(fetched);
          setExpandedErroredId(fetched[0]?._id ?? null);
          setExpandedLogId(null);
          setExpandedDomainId(null);
        } else if (activeTab === 'logs') {
          const response = await devService.getApiLogs();
          const fetchedLogs = response.logs || [];
          setLogs(fetchedLogs);
          // Auto-expand first log when switching to logs tab
          if (fetchedLogs.length > 0) {
            setExpandedLogId(fetchedLogs[0].id);
          } else {
            setExpandedLogId(null);
          }
          // Reset domain expansion when switching to logs
          setExpandedDomainId(null);
        } else {
          const response = await devService.getNotSupportedDomains();
          const fetchedDomains = response.domains || [];
          setDomains(fetchedDomains);
          // Auto-expand first domain when switching to domains tab
          if (fetchedDomains.length > 0) {
            setExpandedDomainId(fetchedDomains[0]._id);
          } else {
            setExpandedDomainId(null);
          }
          // Reset log expansion when switching to domains
          setExpandedLogId(null);
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to fetch data');
        console.error('Dev tool fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [activeTab, isAdmin]);

  if (!isAdmin) {
    return (
      <div className="dev-tool-page">
        <div className="dev-tool-access-denied">
          <div className="access-denied-icon">🔒</div>
          <h2>Access Denied</h2>
          <p>This tool is only available for administrators.</p>
        </div>
      </div>
    );
  }

  const formatTimestamp = (date) => {
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = String(d.getFullYear()).slice(-2);
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  const getMethodColor = (method) => {
    const colors = {
      GET: '#10b981',
      POST: '#3b82f6',
      PUT: '#f59e0b',
      DELETE: '#ef4444',
      PATCH: '#8b5cf6',
    };
    return colors[method] || '#6b7280';
  };

  const generateCurlCommand = (log) => {
    // Use the actual curl from the log if available, otherwise generate a fallback
    if (log.curl) {
      return log.curl;
    }
    const baseUrl = window.location.origin;
    return `curl -X ${log.method} \\
  "${baseUrl}${log.endpoint}" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer YOUR_TOKEN"`;
  };

  const copyToClipboard = (text, logId) => {
    navigator.clipboard.writeText(text);
    setCopiedLogId(logId);
    setTimeout(() => setCopiedLogId(null), 2000);
  };

  const toggleExpand = (logId) => {
    const isExpanding = expandedLogId !== logId;
    setExpandedLogId(isExpanding ? logId : null);

    // Scroll to details on mobile
    if (window.innerWidth <= 768 && isExpanding) {
      setTimeout(() => {
        logsDetailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };

  const toggleDomainExpand = (domainId) => {
    const isExpanding = expandedDomainId !== domainId;
    setExpandedDomainId(isExpanding ? domainId : null);
    // A pending confirm belongs to the panel that was open, not the next one
    setConfirmDeleteDomainId(null);

    // Scroll to details on mobile
    if (window.innerWidth <= 768 && isExpanding) {
      setTimeout(() => {
        domainsDetailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };

  // Only parked fetch failures can be deleted - the rest of this list is real
  // news, and the server refuses to delete it whatever we send
  const deletableRows = erroredNews.filter((item) => item.deletable);

  const toggleErroredExpand = (erroredId) => {
    const isExpanding = expandedErroredId !== erroredId;
    setExpandedErroredId(isExpanding ? erroredId : null);

    // Scroll to details on mobile
    if (window.innerWidth <= 768 && isExpanding) {
      setTimeout(() => {
        erroredDetailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };

  const getFaviconUrl = (domain) => {
    // Extract domain name without protocol and path
    const domainName = domain.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
    return `https://www.google.com/s2/favicons?domain=${domainName}&sz=32`;
  };

  const copyLinkToClipboard = (link, linkId) => {
    navigator.clipboard.writeText(link);
    setCopiedLinkId(linkId);
    setTimeout(() => setCopiedLinkId(null), 2000);
  };

  /**
   * Drop one recorded URL from a domain's history
   * The server recomputes the domain's strikes, so removing the links behind a
   * block lifts it - and removing the last one drops the domain entirely,
   * which is why the response can come back without a domain.
   */
  const deleteDomainUrl = async (domain, urlId) => {
    setDeletingLinkId(urlId);
    setError(null);

    try {
      const response = await devService.removeNotSupportedDomainUrl(domain.domain, urlId);

      if (response.domainCleared) {
        const next = domains.filter((d) => d._id !== domain._id);
        setDomains(next);
        // The history panel renders by id, so move focus onto a domain that
        // still exists rather than leaving an empty panel open
        setExpandedDomainId(next[0]?._id ?? null);
        return;
      }

      setDomains((prev) => prev.map((d) => (d._id === domain._id ? response.domain : d)));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove URL');
      console.error('Remove domain URL error:', err);
    } finally {
      setDeletingLinkId(null);
    }
  };

  /**
   * Drop a domain along with its whole URL history
   * Clearing a working publisher one link at a time is busywork, so this is the
   * bulk path. The per-URL remove stays for the narrow case - a single bad
   * paste that needs to go without discarding the rest of the evidence.
   */
  const deleteDomain = async (domain) => {
    setDeletingDomainId(domain._id);
    setError(null);

    try {
      await devService.removeNotSupportedDomain(domain.domain);

      const next = domains.filter((d) => d._id !== domain._id);
      setDomains(next);
      // The history panel renders by id, so move focus onto a domain that
      // still exists rather than leaving an empty panel open
      setExpandedDomainId(next[0]?._id ?? null);
      setConfirmDeleteDomainId(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove domain');
      console.error('Remove domain error:', err);
      // Leave the overlay up on failure so the error is read next to the
      // action that caused it, instead of the panel snapping back as if the
      // delete went through
    } finally {
      setDeletingDomainId(null);
    }
  };

  /**
   * Open the confirmation for a domain-wide delete
   * The overlay is absolute inside the panel, which is itself the scroll
   * container, so it anchors to the top of the history - reset the scroll or a
   * mid-scroll click leaves the buttons sitting off screen.
   */
  const askDeleteDomain = (domain) => {
    setConfirmDeleteDomainId(domain._id);
    domainsDetailsRef.current?.scrollTo({ top: 0 });
  };

  // Arm a row's delete - the confirmation renders over the row itself
  const startDeleteErroredNews = (item, e) => {
    e.stopPropagation();
    setConfirmingErroredId(item._id);
  };

  const cancelDeleteErroredNews = (e) => {
    e?.stopPropagation();
    setConfirmingErroredId(null);
  };

  /**
   * Delete one parked fetch failure
   * The server only deletes rows that are actually `failed`, so a stale id -
   * or a row that has since become news - comes back as not found rather than
   * removing real news.
   */
  const deleteErroredNewsItem = async (item, e) => {
    e?.stopPropagation();
    setDeletingErroredId(item._id);
    setError(null);

    try {
      await devService.deleteErroredNews(item._id);

      const next = erroredNews.filter((f) => f._id !== item._id);
      setErroredNews(next);
      // The details panel renders by id, so move focus onto a row that still
      // exists rather than leaving an empty panel open
      if (expandedErroredId === item._id) {
        setExpandedErroredId(next[0]?._id ?? null);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete record');
      console.error('Delete errored news error:', err);
    } finally {
      setDeletingErroredId(null);
      setConfirmingErroredId(null);
    }
  };

  /**
   * Drop every parked fetch failure
   * These pile up on each bad paste, so deleting them one by one is busywork -
   * same reasoning as the domain list's bulk remove. Rows that became real
   * news stay, both here and on the server.
   */
  const clearAllErroredNews = async () => {
    setClearingErrored(true);
    setError(null);

    try {
      await devService.clearErroredNews();
      const kept = erroredNews.filter((item) => !item.deletable);
      setErroredNews(kept);
      setExpandedErroredId(kept[0]?._id ?? null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to clear records');
      console.error('Clear errored news error:', err);
    } finally {
      setClearingErrored(false);
      setConfirmingClearAll(false);
    }
  };

  const getErrorColor = (errorMessage) => {
    if (errorMessage?.includes('403')) return '#ef4444';
    if (errorMessage?.includes('401')) return '#f59e0b';
    if (errorMessage?.includes('451')) return '#8b5cf6';
    if (errorMessage?.includes('Could not extract')) return '#06b6d4';
    return '#6b7280';
  };

  return (
    <div className="dev-tool-page">
      {/* Horizontal Tabs */}
      <div className="dev-tool-layout">
        <div className="dev-tool-tabs">
          <button
            className={`dev-tool-tab ${activeTab === 'domains' ? 'dev-tool-tab--active' : ''}`}
            onClick={() => setActiveTab('domains')}
          >
            Not Supported Domains
          </button>
          <button
            className={`dev-tool-tab ${activeTab === 'errored' ? 'dev-tool-tab--active' : ''}`}
            onClick={() => setActiveTab('errored')}
          >
            Errored News
          </button>
          <button
            className={`dev-tool-tab ${activeTab === 'logs' ? 'dev-tool-tab--active' : ''}`}
            onClick={() => setActiveTab('logs')}
          >
            API Logs
          </button>
        </div>

        {/* Tab Content */}
        <div className="dev-tool-content-area">
          {loading ? (
            <div className="dev-tool-loading">
              <Loader />
            </div>
          ) : error ? (
            <div className="dev-tool-error">
              <p>{error}</p>
            </div>
          ) : activeTab === 'errored' ? (
            /* Errored News Tab - everything that hit trouble fetching its
               article, whether or not it ended up as news */
            <div className="dev-tool-errored">
              {/* No heading: the tab above already names this screen */}
              <div className="dev-tool-errored-header">
                {deletableRows.length > 0 && (
                  confirmingClearAll ? (
                    <div className="errored-confirm-inline">
                      <span className="errored-confirm-text">
                        Sure? Delete all {deletableRows.length} failed record{deletableRows.length === 1 ? '' : 's'}?
                      </span>
                      <button
                        className="errored-confirm-yes"
                        onClick={clearAllErroredNews}
                        disabled={clearingErrored}
                      >
                        {clearingErrored ? 'Deleting...' : 'Yes'}
                      </button>
                      <button
                        className="errored-confirm-no"
                        onClick={() => setConfirmingClearAll(false)}
                        disabled={clearingErrored}
                      >
                        No
                      </button>
                    </div>
                  ) : (
                    <button
                      className="domain-delete-btn"
                      disabled={clearingErrored}
                      title="Delete every failed record from the database - news is kept"
                      onClick={() => setConfirmingClearAll(true)}
                    >
                      {clearingErrored ? (
                        <Loader2 size={12} className="url-delete-spinner" />
                      ) : (
                        <Trash2 size={12} />
                      )}
                      Clear failed
                    </button>
                  )
                )}
              </div>
              <p className="dev-tool-errored-hint">
                Every news record carrying a fetch error, whatever its status - including the ones
                the user&apos;s browser had to fetch because the server was refused. URLs that never
                became news are hidden from the listings, so this is the only place they appear.
              </p>
              {erroredNews.length === 0 ? (
                <p className="dev-tool-empty">No errored news found</p>
              ) : (
                <div className="swagger-logs-horizontal">
                  {/* Left: Errored URL list */}
                  <div className="swagger-logs-list">
                    {erroredNews.map((item) => (
                      <div
                        key={item._id}
                        className={`errored-item ${expandedErroredId === item._id ? 'errored-item--active' : ''}`}
                        onClick={() => toggleErroredExpand(item._id)}
                      >
                        <div className="errored-item-main">
                          <img
                            src={getFaviconUrl(item.domain || item.sourceUrl)}
                            alt=""
                            className="errored-favicon"
                            onError={(e) => { e.target.style.visibility = 'hidden'; }}
                          />
                          <div className="errored-item-info">
                            <div className="errored-item-domain">{item.domain || 'unknown'}</div>
                            {item.title && <div className="errored-item-title">{item.title}</div>}
                            <div className="errored-item-url">{item.sourceUrl}</div>
                            <div className="errored-item-meta">
                              <span>{formatTimestamp(item.createdAt)}</span>
                              {item.initiatedBy && <span className="errored-item-user">{item.initiatedBy}</span>}
                              {/* The list is no longer all 'failed', so which
                                  rows are dead ends has to be on the row */}
                              {item.status && <span className="errored-item-status">{item.status}</span>}
                            </div>
                          </div>
                          <span
                            className={`errored-count-badge ${item.deletable ? '' : 'errored-count-badge--survived'}`}
                          >
                            {item.fetchErrors.length} error{item.fetchErrors.length === 1 ? '' : 's'}
                          </span>
                          {item.deletable ? (
                            <button
                              className="errored-delete-btn"
                              disabled={deletingErroredId === item._id}
                              title="Delete this failed record"
                              // The row itself toggles the details panel
                              onClick={(e) => startDeleteErroredNews(item, e)}
                            >
                              {deletingErroredId === item._id ? (
                                <Loader2 size={12} className="url-delete-spinner" />
                              ) : (
                                <Trash2 size={12} />
                              )}
                            </button>
                          ) : (
                            /* Real news: there is something to open, and
                               nothing here to delete */
                            <a
                              className="errored-open-btn"
                              href={`/dashboard/news/${item._id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="Open this news"
                              // The row itself toggles the details panel
                              onClick={(e) => e.stopPropagation()}
                            >
                              <ExternalLink size={12} />
                            </a>
                          )}
                          <div className="swagger-expand-btn">
                            {expandedErroredId === item._id ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                          </div>
                        </div>

                        {confirmingErroredId === item._id && (
                          <div
                            className="errored-confirm-overlay"
                            onClick={(e) => cancelDeleteErroredNews(e)}
                          >
                            <span className="errored-confirm-text">
                              Delete this failed record for {item.domain || item.sourceUrl}?
                            </span>
                            <div
                              className="errored-confirm-buttons"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                className="errored-confirm-yes"
                                onClick={(e) => deleteErroredNewsItem(item, e)}
                                disabled={deletingErroredId === item._id}
                              >
                                {deletingErroredId === item._id ? 'Deleting...' : 'Yes'}
                              </button>
                              <button
                                className="errored-confirm-no"
                                onClick={(e) => cancelDeleteErroredNews(e)}
                                disabled={deletingErroredId === item._id}
                              >
                                No
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Right: Error details */}
                  {expandedErroredId && (
                    <div ref={erroredDetailsRef} className="swagger-details-panel">
                      {(() => {
                        const item = erroredNews.find((f) => f._id === expandedErroredId);
                        if (!item) return null;
                        return (
                          <>
                            {/* The odd case worth calling out on sight: there
                                are errors on this row, yet the article was
                                read - by the browser, not the server */}
                            {item.articleSource === 'frontend' && (
                              <div className="errored-rescued-note">
                                <Globe size={14} />
                                <span>
                                  The server was refused by this publisher - the article was read by
                                  {' '}{item.initiatedBy || 'the user'}&apos;s browser instead.
                                </span>
                              </div>
                            )}

                            {/* Worse than the above: nothing could fetch this
                                page at all, so somebody had to paste its source
                                in by hand to get a short out of it */}
                            {item.articleSource === 'manual' && (
                              <div className="errored-rescued-note">
                                <Globe size={14} />
                                <span>
                                  Neither the server nor the browser could fetch this page -
                                  {' '}{item.initiatedBy || 'the user'} pasted its page source in by hand.
                                </span>
                              </div>
                            )}

                            <div className="swagger-detail-section">
                              <div className="swagger-detail-header">
                                <span className="swagger-detail-label">URL:</span>
                                <button
                                  className="swagger-copy-btn"
                                  onClick={() => copyLinkToClipboard(item.sourceUrl, item._id)}
                                >
                                  {copiedLinkId === item._id ? (
                                    <><Check size={14} />Copied</>
                                  ) : (
                                    <><Copy size={14} />Copy</>
                                  )}
                                </button>
                              </div>
                              <pre className="swagger-curl-code">{item.sourceUrl}</pre>
                            </div>

                            <div className="swagger-detail-section">
                              <span className="swagger-detail-label">Initiated by:</span>
                              <div className="errored-detail-value">{item.initiatedBy || 'unknown'}</div>
                            </div>

                            <div className="swagger-detail-section">
                              <span className="swagger-detail-label">News status:</span>
                              <div className="errored-detail-value">
                                {item.status || 'unknown'}
                                {item.deletable ? ' - never became news' : ''}
                              </div>
                            </div>

                            <div className="swagger-detail-section">
                              <span className="swagger-detail-label">
                                Errors ({item.fetchErrors.length}):
                              </span>
                              <div className="errored-error-list">
                                {item.fetchErrors.length === 0 && (
                                  /* Only happens when the browser retry lost
                                     the id of the parked record - the server's
                                     error is then a separate row in this list */
                                  <div className="errored-error-empty">
                                    No error recorded on this record
                                  </div>
                                )}
                                {item.fetchErrors.map((fe, index) => (
                                  <div key={index} className="errored-error-row">
                                    <span className={`errored-source-badge errored-source-badge--${fe.source}`}>
                                      {fe.source}
                                    </span>
                                    <div className="errored-error-body">
                                      <div className="errored-error-reason">
                                        {fe.reason || 'unknown'}
                                        {fe.statusCode ? ` (HTTP ${fe.statusCode})` : ''}
                                      </div>
                                      {fe.message && (
                                        <div className="errored-error-message">{fe.message}</div>
                                      )}
                                      <div className="errored-error-time">{formatTimestamp(fe.at)}</div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : activeTab === 'logs' ? (
            /* API Logs Tab - Swagger Style */
            <div className="dev-tool-logs-swagger">
              <h3>API Logs</h3>
              {logs.length === 0 ? (
                <p className="dev-tool-empty">No logs found</p>
              ) : (
                <div className="swagger-logs-horizontal">
                  {/* Left: Logs List */}
                  <div className="swagger-logs-list">
                    {logs.map((log) => (
                      <div
                        key={log.id}
                        className={`swagger-log-item ${expandedLogId === log.id ? 'swagger-log-item--active' : ''}`}
                        onClick={() => toggleExpand(log.id)}
                      >
                        {/* Main log row */}
                        <div className="swagger-log-main">
                          {/* Column 1: API Info */}
                          <div className="swagger-api-info">
                            <div className="swagger-api-row">
                              <span
                                className="swagger-method-badge"
                                style={{ background: getMethodColor(log.method) }}
                              >
                                {log.method}
                              </span>
                              <span className="swagger-endpoint">{log.endpoint}</span>
                            </div>
                            <div className="swagger-timestamp-row">
                              <span className="swagger-timestamp">Time: {formatTimestamp(log.timestamp)}</span>
                              <span className="swagger-response-time">{log.responseTime}ms</span>
                            </div>
                          </div>

                          {/* Column 2: Execution Time (Desktop only) */}
                          <div className="swagger-execution">
                            <div className="swagger-time">{log.responseTime}ms</div>
                          </div>

                          {/* Column 3: Expand Button */}
                          <div className="swagger-expand-btn">
                            {expandedLogId === log.id ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Right: Details Panel */}
                  {expandedLogId && (
                    <div ref={logsDetailsRef} className="swagger-details-panel">
                      {(() => {
                        const log = logs.find(l => l.id === expandedLogId);
                        if (!log) return null;
                        return (
                          <>
                            {/* CURL Command */}
                            <div className="swagger-detail-section">
                              <div className="swagger-detail-header">
                                <span className="swagger-detail-label">CURL:</span>
                                <button
                                  className="swagger-copy-btn"
                                  onClick={() => copyToClipboard(generateCurlCommand(log), log.id)}
                                >
                                  {copiedLogId === log.id ? (
                                    <>
                                      <Check size={14} />
                                      Copied
                                    </>
                                  ) : (
                                    <>
                                      <Copy size={14} />
                                      Copy
                                    </>
                                  )}
                                </button>
                              </div>
                              <pre className="swagger-curl-code">{generateCurlCommand(log)}</pre>
                            </div>

                            {/* Response */}
                            <div className="swagger-detail-section">
                              <div className="swagger-detail-header">
                                <span className="swagger-detail-label">Response:</span>
                                <button
                                  className="swagger-copy-btn"
                                  onClick={() => copyToClipboard(
                                    typeof log.response === 'object'
                                      ? JSON.stringify(log.response, null, 2)
                                      : typeof log.response === 'string'
                                      ? log.response
                                      : JSON.stringify({
                                          success: log.status >= 200 && log.status < 300,
                                          statusCode: log.status,
                                          message: log.message,
                                          timestamp: new Date(log.timestamp).toISOString()
                                        }, null, 2),
                                    `response-${log.id}`
                                  )}
                                >
                                  {copiedLogId === `response-${log.id}` ? (
                                    <>
                                      <Check size={14} />
                                      Copied
                                    </>
                                  ) : (
                                    <>
                                      <Copy size={14} />
                                      Copy
                                    </>
                                  )}
                                </button>
                              </div>
                              <pre className="swagger-response-code">
{typeof log.response === 'object'
  ? JSON.stringify(log.response, null, 2)
  : typeof log.response === 'string'
  ? log.response
  : JSON.stringify({
      success: log.status >= 200 && log.status < 300,
      statusCode: log.status,
      message: log.message,
      timestamp: new Date(log.timestamp).toISOString()
    }, null, 2)}
                              </pre>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            /* Not Supported Domains Tab */
            <div className="dev-tool-domains">
              <h3>Not Supported Domains</h3>
              {domains.length === 0 ? (
                <p className="dev-tool-empty">No domains found</p>
              ) : (
                <div className="domains-horizontal">
                  {/* Left: Domains List */}
                  <div className="domains-list">
                    {domains.map((domain) => (
                      <div
                        key={domain._id}
                        className={`domain-item ${expandedDomainId === domain._id ? 'domain-item--active' : ''}`}
                        onClick={() => toggleDomainExpand(domain._id)}
                      >
                        <div className="domain-main">
                          {/* Left: Favicon */}
                          <img
                            src={getFaviconUrl(domain.domain)}
                            alt=""
                            className="domain-favicon"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />

                          {/* Middle: Domain name + error below it */}
                          <div className="domain-info">
                            <span className="domain-name" title={domain.domain}>{domain.domain}</span>
                            <span className="domain-time-mobile">{formatTimestamp(domain.createdAt)}</span>
                          </div>

                          {/* Right: Count on top, accordion below */}
                          <div className="domain-right">
                            <div className="domain-attempts-badge">
                              {domain.fullUrl?.length || 0}
                            </div>
                            <div className="domain-expand-btn">
                              {expandedDomainId === domain._id ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Right: History Panel */}
                  {expandedDomainId && (
                    <div ref={domainsDetailsRef} className="domain-history-panel">
                      {(() => {
                        const domain = domains.find(d => d._id === expandedDomainId);
                        if (!domain) return null;
                        const urlCount = domain.fullUrl?.length || 0;
                        return (
                          <>
                            {/* Domain Delete Confirmation Overlay */}
                            {confirmDeleteDomainId === domain._id && (
                              <div
                                className="domain-delete-confirm-overlay"
                                onClick={() => setConfirmDeleteDomainId(null)}
                              >
                                <span className="domain-delete-confirm-text">
                                  Delete {domain.domain} and all {urlCount} URL{urlCount === 1 ? '' : 's'}?
                                </span>
                                <div
                                  className="domain-delete-confirm-buttons"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <button
                                    className="domain-delete-confirm-yes"
                                    onClick={() => deleteDomain(domain)}
                                    disabled={deletingDomainId === domain._id}
                                  >
                                    {deletingDomainId === domain._id ? 'Deleting...' : 'Yes'}
                                  </button>
                                  <button
                                    className="domain-delete-confirm-no"
                                    onClick={() => setConfirmDeleteDomainId(null)}
                                    disabled={deletingDomainId === domain._id}
                                  >
                                    No
                                  </button>
                                </div>
                              </div>
                            )}

                            {/* Domain Header */}
                            <div className="history-header">
                              <div className="history-header-top">
                                <div className="history-header-info">
                                  <img
                                    src={getFaviconUrl(domain.domain)}
                                    alt=""
                                    className="history-favicon"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                  <span className="history-domain-name">{domain.domain}</span>
                                </div>
                                <div className="history-header-actions">
                                  <div className="history-total">
                                    {urlCount} URLs
                                  </div>
                                  <button
                                    className="domain-delete-btn"
                                    disabled={deletingDomainId === domain._id}
                                    title="Remove this domain and its entire URL history"
                                    onClick={() => askDeleteDomain(domain)}
                                  >
                                    <Trash2 size={12} />
                                    Remove all
                                  </button>
                                </div>
                              </div>
                              <div className="history-error-text">{domain.errorOccurred?.message}</div>
                            </div>

                            {/* URL History List */}
                            <div className="history-urls-section">
                              <div className="history-urls-label">URL History:</div>
                              <div className="history-urls-list">
                                {domain.fullUrl?.length > 0 ? (
                                  domain.fullUrl.map((url, index) => (
                                    <div key={url._id} className="history-url-item">
                                      <div className="url-header">
                                        <span className="url-index">#{domain.fullUrl.length - index}</span>
                                        <span className="url-date">{formatTimestamp(url.createdTime)}</span>
                                        <span className="url-by">{url.createdBy}</span>
                                      </div>
                                      <div className="url-link-wrapper">
                                        <a
                                          href={url.link}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="url-link"
                                        >
                                          {url.link}
                                        </a>
                                        <button
                                          className="url-copy-btn"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            copyLinkToClipboard(url.link, url._id);
                                          }}
                                        >
                                          {copiedLinkId === url._id ? (
                                            <>
                                              <Check size={12} />
                                              Copied
                                            </>
                                          ) : (
                                            <>
                                              <Copy size={12} />
                                              Copy
                                            </>
                                          )}
                                        </button>
                                        <button
                                          className="url-delete-btn"
                                          disabled={deletingLinkId === url._id}
                                          title={
                                            domain.fullUrl.length === 1
                                              ? 'Remove this URL and clear the domain'
                                              : 'Remove this URL from the history'
                                          }
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            deleteDomainUrl(domain, url._id);
                                          }}
                                        >
                                          {deletingLinkId === url._id ? (
                                            <Loader2 size={12} className="url-delete-spinner" />
                                          ) : (
                                            <Trash2 size={12} />
                                          )}
                                          Remove
                                        </button>
                                      </div>
                                    </div>
                                  ))
                                ) : (
                                  <div className="history-urls-empty">No URL history available</div>
                                )}
                              </div>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DevTool;
